import { Controller } from "react-hook-form";
import { Box, Tooltip, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import { displayFlex, flexAllCentre } from "../../styles/mui/styles/display";
import { FormInputProps } from "./FormInputProps";
import colors from "../../styles/colors/colors";
import fontWeight from "../../styles/mui/fontWeight";
import FormInputIcons from "./FormInputIcons";
import { checkValidationMsgSymbol, handleInputEvent, getValue } from "../../utils";
import { InputTextLabel } from "../styles/styles";
import { NotesTooltip, NotesTooltipStyle } from "../../modules/AllPatientRecords/routes/AuthorizationDetails/styles/NotesTable";

export const FormInputText = ({
  control,
  disabled,
  handleChange,
  helper,
  iconName,
  isOnlyIcon,
  label,
  maxRow,
  multiline,
  name,
  onKeyPress,
  placeholder,
  searchInputRef,
  textLength,
  inputType,
  mandatorySymbol,
  InputStyle,
  inputVariant,
  inputRows,
  readOnly,
  hideRequiredSymbol,
  shouldTrimOnBlur = false,
  handleBlur
}: FormInputProps) => {
  let props = {};
  if (inputRows) {
    props = {
      rows: inputRows,
      multiline: true,
    };
  } else if (maxRow != undefined) {
    props = {
      maxRows: maxRow || 2,
      multiline: true,
    };
  } else {
    props = {
      multiline: multiline,
    };
  }
  const renderErrorHelperText = (error: { message?: string } | undefined): React.ReactNode => {
    if (!error || !error.message) return "";
    if (error.message.length > 20) {
      return (
        <Tooltip
          title={
            <Typography
              variant="subtitle1"
              fontWeight={fontWeight.Weight[4]}
              sx={{ textAlign: "right" }}
            >
              <Box
                dangerouslySetInnerHTML={{
                  __html: error.message,
                }}
              />
            </Typography>
          }
          arrow
          slotProps={{
            popper: {
              sx: NotesTooltipStyle,
            },
          }}
        >
          <Typography sx={NotesTooltip}>{error.message}</Typography>
        </Tooltip>
      );
    }
    return error.message;
  };
  
  const required =
    checkValidationMsgSymbol(getValue(helper, "required.message", " ")) &&
    getValue(helper, "required.value")
      ? true
      : false;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={""}
      rules={helper}
      render={({
        field: { onChange, value, ref },
        fieldState: { error },
      }) => (
        <TextField
          required={required}
          error={error !== undefined}
          helperText={renderErrorHelperText(error)}
          {...props}
          onChange={(e) => {
            onChange(e);
            handleChange && handleChange(e);
          }}
          onBlur={(e) => {
            if (shouldTrimOnBlur) {
              const trimmedValue = (e.target.value || "").replace(/(?<=\S)\s+$|^\s+$/, "");
              onChange(trimmedValue);
            }
            handleBlur && handleBlur(); // call RHF blur
          }}
          value={value || ""} // handle null/undefined
          onKeyUp={(e) => {
            onKeyPress && onKeyPress(e);
          }}
          onInput={(e) => {
            handleInputEvent(e, inputType);
          }}
          fullWidth
          sx={InputStyle ? InputStyle : InputTextLabel}
          label={
            label && (
              <Box sx={displayFlex}>
                <Typography
                  variant="h5"
                  fontWeight={fontWeight.Weight[3]}
                  sx={{ color: colors.fonts[2] }}
                >
                  {label}
                </Typography>
                {!getValue(helper, "required.value", "") && mandatorySymbol && (
                  <Typography
                    variant="h3"
                    fontWeight={fontWeight.Weight[3]}
                    sx={{ color: colors.red[100] }}
                    pl={"0.4rem"}
                  >
                    *
                  </Typography>
                )}
              </Box>
            )
          }
          placeholder={
            name.includes("streetName1") || name.includes("streetName2")
              ? "Add here..."
              : placeholder
          }
          disabled={disabled}
          slotProps={{
            htmlInput: {
              maxLength: textLength ? textLength : 50,
              ref: searchInputRef ? searchInputRef : null,
              autoComplete: "none",
            },
            inputLabel: { shrink: true },
            input: {
              readOnly: readOnly ? true : false,
              required: !hideRequiredSymbol && required,
              startAdornment: iconName && (
                <Stack direction="row" sx={flexAllCentre}>
                  {FormInputIcons(iconName, isOnlyIcon)}
                </Stack>
              ),
            },
            
          }} // text
          variant={inputVariant ? "outlined" : "standard"}
          inputRef={ref}
          data-testid={name}
        />
      )}
    />
  );
};
